import useCustomerAction from '../hooks/useCustomerAction';
import SellerShopCard from '../components/SellerShopCard';
import ProductImage from '../components/ProductImage';
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productApi } from '../services/productApi';
import { useCart } from '../context/CartContext';
import ReviewSection from '../components/ReviewSection';
import QuestionSection from '../components/QuestionSection';
import { getImageUrls } from '../utils/image';
import {
  normalizeProduct,
  getRetailPrice,
  getDiscount,
  getAvailableVariants,
} from '../services/productService';
import config from '../config';

function ProductPage() {
  const { id } = useParams();
  const authorize = useCustomerAction();
  const [purchaseNotice, setPurchaseNotice] = useState('');
  const [product, setProduct] = useState(null);
  const [bundleItems, setBundleItems] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [added, setAdded] = useState(false);
  const [currentImg, setCurrentImg] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [activeTab, setActiveTab] = useState('reviews');
  const [reviewsCount, setReviewsCount] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [questionsCount, setQuestionsCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const intervalRef = useRef(null);
  const { dispatch } = useCart();
  const navigate = useNavigate();

  const customerProfile = JSON.parse(localStorage.getItem('customer'));
  const selectedPoint =
    customerProfile?.savedPoints?.[customerProfile.savedPoints.length - 1] ||
    null;

  useEffect(() => {
    productApi
      .getById(id)
      .then((data) => {
        const normalized = normalizeProduct(data);
        setProduct(normalized);
        if (normalized.variants && normalized.variants.length > 0) {
          const available =
            getAvailableVariants(normalized.variants)[0] || null;
          if (available) setSelectedVariant(available);
        }
        const gid = normalized.groupId;
        if (gid) {
          productApi
            .getGroup(gid)
            .then((r) => {
              const list = Array.isArray(r) ? r : r.products || [];
              setBundleItems(list.filter((p) => p.id !== parseInt(id)));
            })
            .catch(() => setBundleItems([]));
        } else {
          setBundleItems([]);
        }
        productApi
          .getReviews(id)
          .then((r) => {
            if (r?.length) {
              setReviewsCount(r.length);
              setAvgRating(
                (
                  r.reduce((sum, rev) => sum + rev.rating, 0) / r.length
                ).toFixed(1)
              );
            }
          })
          .catch(() => {});
        productApi
          .getQuestions(id)
          .then((q) => {
            if (q?.length) setQuestionsCount(q.length);
          })
          .catch(() => {});
      })
      .catch(() => setProduct(null));
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const urls = getImageUrls(product);
    if (isHovering && urls.length > 1) {
      intervalRef.current = setInterval(
        () => setCurrentImg((prev) => (prev + 1) % urls.length),
        1900
      );
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isHovering, product]);

  if (!product)
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        Товар не найден
      </div>
    );

  const fullImageUrls = getImageUrls(product);
  const displayPrice =
    selectedVariant?.retailPrice ?? getRetailPrice(product, selectedVariant);
  const oldPrice = product.oldPrice;
  const discountPercent =
    selectedVariant?.discountPercent ?? getDiscount(product, displayPrice);

  const addToCart = () => {
    if (!selectedVariant || selectedVariant.stock <= 0 || getRetailPrice(product, selectedVariant) <= 0) { setPurchaseNotice('Выберите доступный размер'); return; }
    authorize({ type: 'cart', productId: product.id, variantId: selectedVariant.id }, () => {
      dispatch({ type: 'ADD_TO_CART', payload: { ...product, selectedVariantId: selectedVariant.id, selectedSize: selectedVariant.size, price: getRetailPrice(product, selectedVariant), stock: selectedVariant.stock } });
      setPurchaseNotice('Товар добавлен в корзину'); setAdded(true); setTimeout(() => setAdded(false), 1500);
    });
  };
  const buyNow = () => {
    if (!selectedVariant || selectedVariant.stock <= 0 || getRetailPrice(product, selectedVariant) <= 0) { setPurchaseNotice('Выберите доступный размер'); return; }
    authorize({ type: 'buy-now', productId: product.id, variantId: selectedVariant.id }, () => {
      dispatch({ type: 'ADD_TO_CART', payload: { ...product, selectedVariantId: selectedVariant.id, selectedSize: selectedVariant.size, price: getRetailPrice(product, selectedVariant), stock: selectedVariant.stock } });
      dispatch({ type: 'SELECT_ONLY', payload: selectedVariant.id });
      navigate('/checkout');
    });
  };
  const publicArticle = product.publicArticle || product.platformSku;
  const copySku = () => {
    navigator.clipboard.writeText(publicArticle || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const deliveryDays = selectedPoint?.deliveryDays;

  // Клиентская логика: разделяем размеры на доступные и недоступные
  const sortedVariants = product.variants || [];
  const availableVariants = sortedVariants.filter((v) => v.stock > 0);
  const unavailableVariants = sortedVariants.filter((v) => v.stock === 0);
  const allVariants = [...availableVariants, ...unavailableVariants];

  return (
    <div className="product-page-wb">
      <div className="product-gallery-wb">
        <div
          className="main-image-container"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <ProductImage product={product} src={fullImageUrls[currentImg]} width="100%" height="100%" className="main-image-wb" loading="eager" />
          {fullImageUrls.length > 1 && (
            <>
              <button
                className="arrow-wb left"
                onClick={() =>
                  setCurrentImg(
                    (prev) =>
                      (prev - 1 + fullImageUrls.length) % fullImageUrls.length
                  )
                }
              >
                ‹
              </button>
              <button
                className="arrow-wb right"
                onClick={() =>
                  setCurrentImg((prev) => (prev + 1) % fullImageUrls.length)
                }
              >
                ›
              </button>
            </>
          )}
        </div>
        {fullImageUrls.length > 1 && (
          <div className="thumbnails-wb">
            {fullImageUrls.map((url, idx) => (
              <button type="button" key={idx} className={`product-gallery-thumb ${idx === currentImg ? 'active' : ''}`} aria-label={`Показать фото ${idx + 1}`} aria-pressed={idx === currentImg} onClick={() => setCurrentImg(idx)}><ProductImage product={product} src={url} alt={`Фото ${idx + 1}`} width={54} height={72} /></button>
            ))}
          </div>
        )}
      </div>

      <div className="product-center-info">
        {product.brand && <p className="product-brand">{product.brand}</p>}
        <h1 className="product-title-wb">{product.title}</h1>{(purchaseNotice || authorize.error) && <p className="purchase-notice" role="status">{authorize.error || purchaseNotice}</p>}
        <div className="product-feedback">
          <span className="feedback-item">
            <span className="star-icon">★</span> {avgRating} · {reviewsCount}{' '}
            оценок
          </span>
          <span className="feedback-item">{questionsCount} вопросов</span>
        </div>
        {product.color && (
          <p className="product-color">Цвет: {product.color}</p>
        )}

        {bundleItems.length > 0 && (
          <div className="bundle-colors-inline">
            {bundleItems.map((b) => {

              return (
                <Link
                  to={`/product/${b.id}`}
                  key={b.id}
                  className="bundle-color-square"
                  title={b.color || b.title}
                >
                  <ProductImage product={b} alt={b.color || b.title} width="100%" height="100%" />
                </Link>
               );
            })}
          </div>
        )}

        {allVariants.length > 0 && (
          <div className="sizes-block-wb">
            <p className="sizes-title-wb">Размер:</p>
            <div className="sizes-list-wb">
              {allVariants.map((v) => {
                const isAvailable = v.stock > 0;
                const isSelected = selectedVariant?.id === v.id;
                return (
                  <button
                    key={v.id}
                    className={`size-btn-wb ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                    disabled={!isAvailable}
                    onClick={() => {
                      if (isAvailable) setSelectedVariant(v);
                    }}
                  >
                    <span className="size-value">{v.size}</span>
                    {/* Надпись "Нет" удалена, просто неактивная кнопка */}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {publicArticle && (
          <div className="sku-block">
            <span className="sku-label">Артикул: </span>
            <button className="sku-copy-btn" onClick={copySku}>
              {publicArticle} {copied ? '✓' : '📋'}
            </button>
          </div>
        )}
        <button
          className="btn btn-outline characteristics-btn"
          onClick={() => alert('Характеристики (заглушка)')}
        >
          Все характеристики
        </button>
        <div className="product-badges">
          <span className="badge">Выберите подходящий размер</span>
        </div>
      </div>

      <div className="product-right-block">
        <div className="price-block-wb">
          {oldPrice && <span className="old-price-wb">{oldPrice} ₽</span>}
          <span className="current-price-wb">{displayPrice} ₽</span>
          {discountPercent > 0 && (
            <span className="discount-badge">-{discountPercent}%</span>
          )}
        </div>
        <div className="actions-block-wb">
          <button
            className="btn btn-primary btn-large buy-btn-wb"
            onClick={buyNow}
          >
            Купить сейчас
          </button>
          <button
            className="btn btn-outline btn-large cart-btn-wb"
            onClick={addToCart}
            disabled={!selectedVariant || selectedVariant.stock === 0}
          >
            {added ? '✓ Добавлено' : 'В корзину'}
          </button>
        </div>
        <div className="delivery-info">
          <p>
            {deliveryDays
              ? `Ориентир доставки: ${deliveryDays} дн.`
              : 'Стоимость и срок доставки — при оформлении'}
          </p>
        </div>
        <SellerShopCard sellerId={product.sellerId || product.seller_id} fallbackName={product.seller?.name || product.sellerName} />
      </div>

      <div className="product-tabs-wb">
        <div className="tab-buttons">
          <button
            className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Отзывы
          </button>
          <button
            className={`tab-btn ${activeTab === 'questions' ? 'active' : ''}`}
            onClick={() => setActiveTab('questions')}
          >
            Вопросы
          </button>
        </div>
        <div className="tab-content">
          {activeTab === 'reviews' && <ReviewSection productId={id} />}
          {activeTab === 'questions' && <QuestionSection productId={id} />}
        </div>
      </div>
    </div>
  );
}

export default ProductPage;
